import React from 'react';
import { FormattedMessage, FormattedDate, useIntl } from '../../util/reactIntl';
import {
  DATE_TYPE_DATE,
  DATE_TYPE_TIME,
  DATE_TYPE_DATETIME,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_NIGHT,
  propTypes,
} from '../../util/types';
import { subtractTime, isDST } from '../../util/dates';

import css from './OrderBreakdown.module.css';

const DSTMaybe = props => {
  const { startDate, endDate, isStart, dateType, timeZone } = props;
  const isDayTimeRange = dateType === DATE_TYPE_DATETIME;
  if (!isDayTimeRange) {
    return null;
  }

  const isStartInDST = isDST(startDate, timeZone);
  const isEndInDST = isDST(endDate, timeZone);
  const isDSTChanged = isStartInDST !== isEndInDST;
  const showDSTMsgForStart = isDSTChanged && isStart && isStartInDST;
  const showDSTMsgForEnd = isDSTChanged && !isStart && isEndInDST;

  return showDSTMsgForStart || showDSTMsgForEnd ? (
    <div className={css.itemLabel}>
      <FormattedMessage id="OrderBreakdown.bookingWithDSTInEffect" />
    </div>
  ) : null;
};

const BookingPeriod = props => {
  const { startDate, endDate, dateType, timeZone } = props;
  const timeZoneMaybe = timeZone ? { timeZone } : null;

  const timeFormatOptions =
    dateType === DATE_TYPE_DATE
      ? {
          weekday: 'long',
        }
      : {
          weekday: 'short',
          hour: 'numeric',
          minute: 'numeric',
        };

  const dateFormatOptions = {
    month: 'short',
    day: 'numeric',
  };

  return (
    <>
      <div className={css.bookingPeriod}>
        <div className={css.bookingPeriodSectionLeft}>
          <div className={css.dayLabel}>
            <FormattedMessage id="OrderBreakdown.bookingStart" />
          </div>
          <div className={css.dayInfo}>
            <FormattedDate value={startDate} {...timeFormatOptions} {...timeZoneMaybe} />
          </div>
          <div className={css.itemLabel}>
            <FormattedDate value={startDate} {...dateFormatOptions} {...timeZoneMaybe} />
          </div>
          <DSTMaybe
            startDate={startDate}
            endDate={endDate}
            isStart={true}
            dateType={dateType}
            timeZone={timeZone}
          />
        </div>

        <div className={css.bookingPeriodSectionRight}>
          <div className={css.dayLabel}>
            <FormattedMessage id="OrderBreakdown.bookingEnd" />
          </div>
          <div className={css.dayInfo}>
            <FormattedDate value={endDate} {...timeFormatOptions} {...timeZoneMaybe} />
          </div>
          <div className={css.itemLabel}>
            <FormattedDate value={endDate} {...dateFormatOptions} {...timeZoneMaybe} />
          </div>
          <DSTMaybe
            startDate={startDate}
            endDate={endDate}
            isStart={false}
            dateType={dateType}
            timeZone={timeZone}
          />
        </div>
      </div>
    </>
  );
};


// Format a "HH:MM" 24-hour string to "9:00 AM" / "5:30 PM" — no Date object, no timezone math.
const formatTimeString = t => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return m === 0
    ? `${h12}:00 ${ampm}`
    : `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const DeliveryBookingPeriod = ({ protectedData, orderData, intl: intlProp }) => {
  // orderData is present at checkout; protectedData is the fallback for the transaction/confirmation page
  const deliveryDate = orderData?.deliveryDate ?? protectedData?.deliveryDate;
  const deliveredByTime = orderData?.deliveredByTime ?? protectedData?.deliveredByTime;
  const returnDate = orderData?.returnDate ?? protectedData?.returnDate;
  const returnPickupTime = orderData?.returnPickupTime ?? protectedData?.returnPickupTime;

  const isRoundTrip = orderData?.roundTrip ?? orderData?.isRoundTrip ?? protectedData?.dockDeliveryRoundTrip ?? protectedData?.isRoundTrip;
  const showReturn = !!isRoundTrip && !!returnDate;

  // Format date from "YYYY-MM-DD": split on '-', build date at noon to avoid any midnight boundary
  // issues, format with intl (no timeZone option — no hours means no UTC shift).
  const formatDate = dateStr => {
    if (!dateStr) return '';
    const [y, mo, d] = dateStr.split('-').map(Number);
    if (!y || !mo || !d) return dateStr;
    return intlProp.formatDate(new Date(y, mo - 1, d, 12, 0, 0), {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  };

  const deliveryDateStr = deliveryDate ? formatDate(deliveryDate) : null;
  const deliveryTimeStr = deliveredByTime ? formatTimeString(deliveredByTime) : null;
  const returnDateStr = showReturn && returnDate ? formatDate(returnDate) : null;
  const returnTimeStr = showReturn && returnPickupTime ? formatTimeString(returnPickupTime) : null;

  return (
    <div className={css.bookingPeriod}>
      <div className={css.bookingPeriodSectionLeft}>
        <div className={css.dayLabel}>Delivery</div>
        {deliveryDateStr ? (
          <div className={css.dayInfo}>
            <span>{deliveryDateStr}</span><br /><span>{deliveryTimeStr}</span>
          </div>
        ) : null}
      </div>

      {showReturn && returnDateStr ? (
        <div className={css.bookingPeriodSectionRight}>
          <div className={css.dayLabel}>Return</div>
          <div className={css.dayInfo}>
            <span>{returnDateStr}</span><br /><span>{returnTimeStr}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

/**
 * A line-item to show booking period for the OrderBreakdown
 *
 * @component
 * @param {Object} props
 * @param {propTypes.booking?} props.booking
 * @param {LINE_ITEM_NIGHT | LINE_ITEM_DAY | LINE_ITEM_HOUR} props.code
 * @param {DATE_TYPE_DATE | DATE_TYPE_TIME | DATE_TYPE_DATETIME} props.dateType
 * @param {string} props.timeZone IANA time zone name
 * @param {string?} props.categoryLevel1
 * @param {boolean?} props.isRoundTrip
 * @returns {JSX.Element} line-item element for the order breakdown
 */
const LineItemBookingPeriod = props => {
  const { booking, code, dateType, timeZone, categoryLevel1, isRoundTrip, protectedData, orderData } = props;
  const intl = useIntl();

  console.log('[LineItemBookingPeriod] orderData:', orderData);

  // dock-ramp-delivery renders DeliveryBookingPeriod which reads from orderData/protectedData
  // directly and does not need a booking entity
  if (!booking && categoryLevel1 !== 'dock-ramp-delivery') {
    return null;
  }

  // Route dock-ramp-delivery before accessing booking.attributes (booking may be undefined)
  if (categoryLevel1 === 'dock-ramp-delivery') {
    return (
      <>
        <div className={css.lineItem}>
          <DeliveryBookingPeriod
            timeZone={timeZone}
            isRoundTrip={isRoundTrip}
            protectedData={protectedData}
            orderData={orderData}
            intl={intl}
          />
        </div>
        <hr className={css.totalDivider} />
      </>
    );
  }

  // Attributes: displayStart and displayEnd can be used to differentiate shown time range
  // from actual start and end times used for availability reservation. It can help in situations
  // where there are preparation time needed between bookings.
  // Read more: https://www.sharetribe.com/api-reference/marketplace.html#bookings
  const { start, end, displayStart, displayEnd } = booking.attributes;
  const localStartDate = displayStart || start;
  const localEndDateRaw = displayEnd || end;

  const showInclusiveEndDate = [LINE_ITEM_DAY].includes(code);
  const endDay = showInclusiveEndDate ? subtractTime(localEndDateRaw, 1, 'days') : localEndDateRaw;

  return (
    <>
      <div className={css.lineItem}>
        <BookingPeriod
          startDate={localStartDate}
          endDate={endDay}
          dateType={dateType}
          timeZone={timeZone}
        />
      </div>
      <hr className={css.totalDivider} />
    </>
  );
};

export default LineItemBookingPeriod;
